<template>
  <div class="ows-shell" data-testid="onboarding-wizard-shell">
    <header class="ows-topbar" data-testid="ows-topbar">
      <StandardButton
        v-if="showBack"
        variant="outline"
        size="small"
        data-testid="ows-back"
        @click="$emit('back')"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M12.5 15L7.5 10L12.5 5"
            stroke="currentColor"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        {{ backLabel }}
      </StandardButton>

      <div class="ows-user" data-testid="ows-user">
        <span class="ows-user-name" data-testid="ows-user-name">{{ displayUser }}</span>
        <ExAvatar
          :picture="userPicture"
          :first-name="userFirstName"
          :last-name="userLastName"
          size="medium"
        />
      </div>
    </header>

    <div class="ows-body">
      <div class="ows-content" data-testid="ows-content">
        <div class="ows-stepper" data-testid="ows-stepper">
          <WelcomeWizardStepper :total-steps="totalSteps" :current-step="currentStep" />
        </div>
        <slot />
      </div>

      <aside v-if="showMedia" class="ows-media" :aria-label="mediaLabel" data-testid="ows-media">
        <slot name="media">
          <WelcomeWizardMediaPanel
            :video-embed-url="videoEmbedUrl"
            :title="videoTitle"
            :caption="videoCaption"
          />
        </slot>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ExAvatar from "@components/common/ExAvatar.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import WelcomeWizardStepper from "@components/WelcomeWizardStepper/WelcomeWizardStepper.vue";
import WelcomeWizardMediaPanel from "@components/WelcomeWizardMediaPanel/WelcomeWizardMediaPanel.vue";

/**
 * ER-12245 — OnboardingWizardShell (MDP-621).
 *
 * The shared onboarding-wizard frame every step renders inside: a top bar (Back,
 * hidden on the first page, plus the signed-in user: email, then avatar), then a two-column body —
 * step content on the left (with the progress stepper above it), step video on
 * the right. It owns no flow logic: step count/position, Back visibility, user
 * identity and media all arrive as props, and the step content is the default
 * slot (WelcomeStep, ProfileStep, ReadinessStep…). Presentational only.
 */
const props = defineProps({
  /** Number of pages the user will see. */
  totalSteps: { type: Number, default: 1 },
  /** 0-based index of the current page. */
  currentStep: { type: Number, default: 0 },
  /** Show the Back button (hidden on the first page). */
  showBack: { type: Boolean, default: false },
  /** Back button label. */
  backLabel: { type: String, default: "Back" },
  /** Signed-in user identity (top bar). */
  userFirstName: { type: String, default: "" },
  userLastName: { type: String, default: "" },
  userPicture: { type: String, default: "" },
  /** Display text next to the avatar (falls back to first + last name). */
  userName: { type: String, default: "" },
  /** Signed-in email; shown next to the avatar when present (ticket scope). */
  userEmail: { type: String, default: "" },
  /** Hide the right-hand media column (steps without a video). */
  showMedia: { type: Boolean, default: true },
  /** Accessible name of the media column (e.g. "Profile badge preview" when the slot is overridden). */
  mediaLabel: { type: String, default: "Step video" },
  /** watchclueso.com embed URL. */
  videoEmbedUrl: { type: String, default: "" },
  /** Video title under the player. */
  videoTitle: { type: String, default: "" },
  /** Video caption under the title. */
  videoCaption: { type: String, default: "" },
});

defineEmits(["back"]);

const userName = computed(() => {
  if (props.userName) {
    return props.userName;
  }
  const parts = [props.userFirstName.trim(), props.userLastName.trim()].filter(Boolean);
  return parts.join(" ") || "";
});

/** The top bar shows the signed-in email, then the avatar. */
const displayUser = computed(() => props.userEmail || userName.value);
</script>

<style scoped>
.ows-shell {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background: #ffffff;
  color: #101828;
  font-family: Inter, sans-serif;
}

.ows-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 68px;
  padding: 16px 24px;
  background: #ffffff;
}

.ows-user {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.ows-user-name {
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.ows-body {
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  flex: 1 1 auto;
  gap: 96px;
  width: 100%;
  padding: 0 24px 24px 160px;
  background: #ffffff;
}

.ows-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 560px;
  flex: 0 0 auto;
  padding: 64px 0 40px;
  background: #ffffff;
}

.ows-stepper {
  width: 100%;
}

.ows-media {
  display: flex;
  width: 600px;
  flex: 0 0 auto;
}
</style>
